/**
 * Persian number, currency and date formatting.
 *
 * Rule of thumb used across the UI: anything a human reads as prose gets
 * Persian digits (۱۲۳); anything a human copies or compares — IPs, versions,
 * spec tables — stays Latin and is wrapped in `.ltr-nums`.
 */

const FA_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"] as const

/** Convert every ASCII digit in a string to its Persian counterpart. */
export function toFaDigits(input: string | number): string {
  return String(input).replace(/\d/g, (d) => FA_DIGITS[Number(d)])
}

/** Convert Persian/Arabic-Indic digits back to ASCII (for form input parsing). */
export function toEnDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
}

/** `۱٬۲۳۴٬۵۶۷` — Persian digits with the Persian thousands separator. */
export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}) {
  return new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 0, ...options }).format(
    value,
  )
}

export type Currency = "IRT" | "IRR" | "USD" | "EUR"

const CURRENCY_LABEL: Record<Currency, string> = {
  IRT: "تومان",
  IRR: "ریال",
  USD: "دلار",
  EUR: "یورو",
}

/**
 * Money for display. Returns the parts separately so callers can typeset the
 * amount and the unit at different sizes — the pattern used in pricing cards.
 */
export function formatPriceParts(amount: number, currency: Currency = "IRT") {
  if (currency === "USD" || currency === "EUR") {
    return {
      amount: new Intl.NumberFormat("fa-IR", { maximumFractionDigits: 2 }).format(amount),
      unit: CURRENCY_LABEL[currency],
    }
  }
  return { amount: formatNumber(amount), unit: CURRENCY_LABEL[currency] }
}

export function formatPrice(amount: number, currency: Currency = "IRT") {
  const { amount: value, unit } = formatPriceParts(amount, currency)
  return `${value} ${unit}`
}

export type BillingCycle = "monthly" | "quarterly" | "annually" | "biennially" | "triennially"

export const BILLING_CYCLE_LABEL: Record<BillingCycle, string> = {
  monthly: "ماهانه",
  quarterly: "سه‌ماهه",
  annually: "سالانه",
  biennially: "دوساله",
  triennially: "سه‌ساله",
}

/** Per-cycle suffix, e.g. «/ ماهانه». */
export function cycleSuffix(cycle: BillingCycle) {
  return BILLING_CYCLE_LABEL[cycle]
}

export const CYCLE_MONTHS: Record<BillingCycle, number> = {
  monthly: 1,
  quarterly: 3,
  annually: 12,
  biennially: 24,
  triennially: 36,
}

/**
 * Price for a cycle given a monthly base. `annualDiscount` is a fraction
 * (0.2 = 20% off) applied to every cycle longer than a month.
 */
export function priceForCycle(
  monthlyPrice: number,
  cycle: BillingCycle,
  annualDiscount = 0,
  quarterlyDiscount = 0,
) {
  const months = CYCLE_MONTHS[cycle]
  const gross = monthlyPrice * months
  if (cycle === "monthly") return Math.round(gross)
  if (cycle === "quarterly") {
    return Math.round(gross * (1 - Math.max(0, quarterlyDiscount)))
  }
  if (annualDiscount <= 0) return Math.round(gross)
  return Math.round(gross * (1 - annualDiscount))
}

/** Jalali (Persian) calendar date, e.g. «۲۲ مرداد ۱۴۰۴». */
export function formatDate(input: string | Date) {
  const date = typeof input === "string" ? new Date(input) : input
  if (Number.isNaN(date.getTime())) return ""
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date)
}

export function formatDateShort(input: string | Date) {
  const date = typeof input === "string" ? new Date(input) : input
  if (Number.isNaN(date.getTime())) return ""
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date)
}

/** ISO-8601 for `<time dateTime>` and structured data. */
export function toIsoDate(input: string | Date) {
  const date = typeof input === "string" ? new Date(input) : input
  return Number.isNaN(date.getTime()) ? "" : date.toISOString()
}

/** «۳ روز پیش» — relative time in Persian. */
export function formatRelative(input: string | Date, now = new Date()) {
  const date = typeof input === "string" ? new Date(input) : input
  if (Number.isNaN(date.getTime())) return ""

  const diffMs = date.getTime() - now.getTime()
  const rtf = new Intl.RelativeTimeFormat("fa-IR", { numeric: "auto" })
  const divisions: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["second", 1000],
    ["minute", 1000 * 60],
    ["hour", 1000 * 60 * 60],
    ["day", 1000 * 60 * 60 * 24],
    ["month", 1000 * 60 * 60 * 24 * 30],
    ["year", 1000 * 60 * 60 * 24 * 365],
  ]

  let chosen: [Intl.RelativeTimeFormatUnit, number] = divisions[0]
  for (const division of divisions) {
    if (Math.abs(diffMs) >= division[1]) chosen = division
  }
  return rtf.format(Math.round(diffMs / chosen[1]), chosen[0])
}

/** Storage/bandwidth sizes: «۵۰ گیگابایت». */
export function formatBytesLabel(value: number, unit: "MB" | "GB" | "TB") {
  const label = { MB: "مگابایت", GB: "گیگابایت", TB: "ترابایت" }[unit]
  return `${formatNumber(value)} ${label}`
}

export function formatPercent(fraction: number) {
  return `${formatNumber(Math.round(fraction * 100))}٪`
}

/** «۹۹٫۹۹٪» — uptime needs decimals, unlike most percentages in the UI. */
export function formatUptime(value: number) {
  return `${new Intl.NumberFormat("fa-IR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}٪`
}
